import { SeverityLevel } from '@microsoft/applicationinsights-web';
import * as signalR from '@microsoft/signalr';
import moment from 'moment';
import { createRef, FC, useEffect, useState } from 'react';
import Clock from 'react-live-clock';
import { useParams } from 'react-router-dom';
import * as api from '../../../api/generated';
import { KendoLoading } from '../../../components';
import * as enums from '../../../enums';
import { DoorScreenDisplayType, ScreenSoundType, screenSoundType } from '../../../enums';
import '../../../styles/evacuation.scss';
import '../../../styles/workplace.scss';
import { ai } from '../../../utils/applicationInsights';
import { IDoorScreenlColorsForm } from '../../administration/organization/generalSettings/design/GeneralSettingsDesignDoorScreen';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import DoorScreenDisplayPageFullView from './DoorScreenDisplayPageFullView';
import { IDisplayLocation, IPractitionerWorkTime } from './models';

const DoorScreenDisplayPage: FC = () => {
    const [displayLocation, setDisplayLocation] = useState<IDisplayLocation | null>(null);
    const { doorScreenId } = useParams<'doorScreenId'>();
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [soundType, setSoundType] = useState<ScreenSoundType>();
    const [displayType, setDisplayType] = useState<DoorScreenDisplayType>();
    const [playSound, setPlaySound] = useState<boolean>(false);
    const [blinkProp, setBlinkProp] = useState<boolean>(false);
    const [arrOfSounds, setArrOfSounds] = useState<string[]>(['']);
    const [soundIndex, setSoundIndex] = useState<number>(0);
    const [soundChar, setSoundChar] = useState<string>('letter');

    const [latestChange, setLatestChange] = useState(String);

    const [ticketState, setTicketState] = useState<number | null>(null);

    const [evacuation, setEvacuation] = useState<boolean>(false);

    const [design, setDesign] = useState<IDoorScreenlColorsForm | null>(null);

    const [practitionerToShow, setPractitionerToShow] = useState<IPractitionerWorkTime | null>(null);

    let audioEl = createRef<HTMLAudioElement>();
    let connection: signalR.HubConnection;
    let firstConnected: NodeJS.Timeout;

    useEffect(() => {
        fetchData();
        connection = initializeSignalR();

        setInterval(() => {
            updatePractitionerDisplay(displayLocation);
        }, 300000);

        return () => {
            if (connection) connection.stop();
        };
    }, []);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const doorScreenLocationResp = await api.display.getInitialDoorScreenDisplayLocation(parseInt(doorScreenId.toString()));
            setDisplayLocation(doorScreenLocationResp);
            if (doorScreenLocationResp) {
                if (doorScreenLocationResp.ticket) {
                    setLatestChange(doorScreenLocationResp.ticket.ticketNumber ?? '');
                    setTicketState(doorScreenLocationResp.ticket?.state);
                }
                updatePractitionerDisplay(doorScreenLocationResp);
                setEvacuation(doorScreenLocationResp.evacuation);
                setDesign({
                    primaryColor: doorScreenLocationResp.primaryColor,
                    secondaryColor: doorScreenLocationResp.secondaryColor
                });
                setSoundType(doorScreenLocationResp!.screenSoundType);
                setDisplayType(doorScreenLocationResp!.displayType);
                const button = document.getElementById('playButton');
                if (button) button!.innerText = doorScreenLocationResp!.screenSoundType.toString();
            }
        } catch (ex) {
            setError(ex.error);
            ai.appInsights.trackException({
                exception: new Error('DoorScreen Error in useEffect fetchData' + ex + ', ' + ex?.error),
                severityLevel: SeverityLevel.Error
            });
        } finally {
            setIsLoading(false);
        }
    };

    const initializeSignalR = () => {
        const conn = new signalR.HubConnectionBuilder()
            .withUrl(`/workplace/?doorScreenId=${doorScreenId}`)
            .withAutomaticReconnect([2000, 3000, 5000, 10000, 20000, 30000, 60000, 60000])
            .build();

        conn.onreconnecting(() => {
            if (connection.state === signalR.HubConnectionState.Reconnecting) {
                setError('Nėra interneto ryšio. Bandoma prisijungti...');
            }
        });

        conn.onreconnected(() => {
            if (connection.state === signalR.HubConnectionState.Connected) {
                setError('Prisijungta. Įkeliamas vaizdas...');
            }

            window.location.reload();
        });
        conn.onclose(async () => {
            if (connection.state === signalR.HubConnectionState.Disconnected) {
                setError('Nėra interneto ryšio.');
            }
        });
        conn.on('onRefreshDoorScreen', () => {
            window.location.reload();
        });

        conn.on('onNextTicket', (newTicketNumber: string, ticketState: number) => {
            try {
                setTicketState(null);
                setBlinkProp(false);
                setPlaySound(false);

                if (newTicketNumber.length === 0) {
                    setLatestChange(newTicketNumber);
                    setTicketState(null);
                }

                setTicketState(ticketState);

                if (ticketState !== enums.ticketState.Serviced && newTicketNumber.length > 0) {
                    setTimeout(() => {
                        setLatestChange(newTicketNumber);
                        setBlinkProp(true);
                        if (screenSoundType.Ring == parseInt(document.getElementById('playButton')!.innerText)) {
                            document.getElementById('playButton')?.click();
                        } else {
                            setPlaySound(true);
                        }
                    }, 10);

                    setArrOfSounds(newTicketNumber.split(''));
                    setSoundIndex(0);
                    setSoundChar(isNaN(parseInt(newTicketNumber.split('')[0])) ? 'letter' : 'number');
                } else if (ticketState === enums.ticketState.Serviced && newTicketNumber.length > 0) {
                    setTicketState(ticketState);
                    setLatestChange(newTicketNumber);
                }
            } catch (ex) {
                ai.appInsights.trackException({
                    exception: new Error('DoorScreen Error in hub connection: onNextTicket' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            }
        });
        conn.on('onChangeSoundType', (type: ScreenSoundType, displayType: DoorScreenDisplayType) => {
            try {
                if (type) {
                    setSoundType(type);
                    const button = document.getElementById('playButton');
                    if (button) button!.innerText = type.toString();
                }
                if (displayType) {
                    setDisplayType(undefined);
                    setDisplayType(displayType);
                }
            } catch (ex) {
                ai.appInsights.trackException({
                    exception: new Error('DoorScreen Error in hub connection: onChangeSoundType' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            }
        });
        conn.on('onChangeLocationStatus', (locationStatus: number) => {
            try {
                setBlinkProp(false);
                setPlaySound(false);
                let locationStatusToSet = locationStatus === enums.locationStatus.Default ? '' : enums.locationStatus.format(locationStatus)!;
                setLatestChange(locationStatusToSet);

                setTicketState(null);
            } catch (ex) {
                ai.appInsights.trackException({
                    exception: new Error('DoorScreen Error in hub connection: onChangeLocationStatus' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            }
        });
        conn.on('onChangeEvacuationState', (isEvacuationEnabled: boolean) => {
            setEvacuation(isEvacuationEnabled);
        });
        conn.on('onChangeDoorScreensDesign', (design: IDoorScreenlColorsForm) => {
            try {
                setDesign(design);
            } catch (ex) {
                ai.appInsights.trackException({
                    exception: new Error('DoorScreen Error in hub connection: onChangeDoorScreensDesign' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            }
        });

        conn.start()
            .then(() => {
                setError(null);
                clearInterval(firstConnected);
            })
            .catch((ex) => {
                firstConnected = setInterval(() => {
                    connection = initializeSignalR();
                }, 90000);
                setError(ex);
                ai.appInsights.trackException({
                    exception: new Error('DoorScreen Error in hub connection: first start' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            });
        return conn;
    };

    useEffect(() => {
        const updateLastConnectionInterval = setInterval(async () => {
            if (!error && connection) {
                try {
                    await connection.invoke('updateDeviceLastConnection', parseInt(doorScreenId.toString()), enums.deviceType.DoorScreen);
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('DoorScreen Error in updateLastConnectionInterval' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            }
        }, 60000);

        return () => {
            clearInterval(updateLastConnectionInterval);
        };
    }, [error]);

    const updatePractitionerDisplay = (data: IDisplayLocation | null) => {
        if (data && data.practitioners) {
            var nowDate = moment().format('YYYY-MM-DD HH:mm');
            var practitioner = data.practitioners.find((x) => x.workStart! <= nowDate && nowDate < x.workEnd!);

            if (practitioner) {
                setFirstLoad(false);
                setPractitionerToShow({
                    firstName: practitioner.firstName,
                    lastName: practitioner.lastName,
                    qualification: practitioner.qualification,
                    workStart: null,
                    workEnd: null
                });
            } else {
                setPractitionerToShow(null);
            }
        }
    };

    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    useEffect(() => {
        var timeout = 30000;
        if (firstLoad) timeout = 3000;

        const updatePractitioner = setInterval(async () => {
            try {
                updatePractitionerDisplay(displayLocation);
            } catch (ex) {
                ai.appInsights.trackException({
                    exception: new Error('Error in DoorScreen updatePractitioner' + ex),
                    severityLevel: SeverityLevel.Error
                });
            }
        }, timeout);

        return () => clearInterval(updatePractitioner);
    });

    function selectNextTrack() {
        try {
            if (arrOfSounds.length && arrOfSounds[soundIndex + 1]) {
                setSoundIndex(soundIndex + 1);
                setSoundChar(isNaN(parseInt(arrOfSounds[soundIndex + 1])) ? 'letter' : 'number');
                setPlaySound(false);
                setTimeout(() => {
                    setPlaySound(true);
                }, 10);
            } else {
                setSoundIndex(0);
                setPlaySound(false);
            }
        } catch (ex) {
            ai.appInsights.trackException({
                exception: new Error('DoorScreen Error in selectNextTrack' + ex + ', ' + ex?.error),
                severityLevel: SeverityLevel.Error
            });
        }
    }

    const playRingBell = (playSound: boolean) => {
        audioEl.current!.muted = false;
        if (playSound) {
            var resp = audioEl.current!.play();
            if (resp !== undefined) {
                resp.then((_) => {}).catch((error) => {
                    console.log(error);
                });
            }
        }
    };

    return (
        <>
            <div className="unselectable">
                <button style={{ display: 'none' }} id="playButton" onClick={() => playRingBell(true)}>
                    Focus input
                </button>
                <audio ref={audioEl} src={`/doorbell.mp3`}></audio>
                {screenSoundType.Spelling == soundType && playSound && (
                    <audio src={`/${soundChar}s/${arrOfSounds[soundIndex]}.mp3`} autoPlay onEnded={() => selectNextTrack()}></audio>
                )}
            </div>

            {isLoading ? (
                <KendoLoading />
            ) : (
                <div className="unselectable">
                    {!error && evacuation && (
                        <div className="evacuation-doorScreen">
                            <div className="evacuation-doorScreen-text">Pavojus</div>
                            <div>
                                <img
                                    className="evacuation-doorScreen-image"
                                    src={process.env.PUBLIC_URL + '/img/bell.png'}
                                    height="100%"
                                    width="auto"
                                />
                            </div>
                        </div>
                    )}
                    {!error && !evacuation && displayLocation && displayType && design && (
                        <div className="workplaceScreenContainer" style={{ backgroundColor: design.primaryColor?.toString() }}>
                            <div
                                className="workplaceInformationBar"
                                style={{ backgroundColor: design.primaryColor?.toString(), color: design.secondaryColor?.toString() }}
                            >
                                <span className="workplaceNumber"> {displayLocation.name} </span>
                                <span className="workplaceClock">
                                    <Clock format={'HH:mm'} ticking={true} timezone={moment().zoneAbbr()} />
                                </span>
                            </div>
                            <DoorScreenDisplayPageFullView
                                showMain={latestChange}
                                blinkProp={blinkProp}
                                ticketState={ticketState}
                                displayType={displayType}
                                design={design}
                                practitionerToShow={practitionerToShow}
                                logo={displayLocation.logo}
                            ></DoorScreenDisplayPageFullView>
                            <div
                                className="screenFooter"
                                style={{ color: design.secondaryColor?.toString(), backgroundColor: design.primaryColor?.toString() }}
                            >
                                {displayType === enums.doorScreenDisplayType.Detailed && latestChange !== '' && (
                                    <>
                                        {practitionerToShow?.firstName} {practitionerToShow?.lastName}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {error && <ShowErrorModal error={error} logout={false}></ShowErrorModal>}
                </div>
            )}
        </>
    );
};

export default DoorScreenDisplayPage;
